import React, { useEffect, useState, useContext } from "react";
import {
  PatientContext,
  UserContext,
} from "../../lib/main-context";

// hooks
import { useHistory, useLocation } from "react-router-dom";
import useListSelector from "../../lib/useListSelector";

// components
import Sallie from "../UIComponents/Sallie";
import ListSelector from "../UIComponents/ListSelector";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// api
import { updateTBIDetail, getTBIFormData } from "../../api/TBIRequests";

// lib
import customizeMessages from "../../lib/customizeMessages";
import { Form } from "react-bootstrap";

// translation
import { useTranslation } from "../../i18n/TranslationProvider";

const RecentTBI2 = ({ setOnboardingPercent }) => {
  const { user } = useContext(UserContext);
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const {
    toggleFromSelectedList,
    switchSingleSelection,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  const onSubmit = async () => {
    const assignedData = {
      injuryFrom: selectedData.tbiFrom[0],
      headHitLocation: selectedData.headHitLocation,
      immediateSymptomsResulting: selectedData.experiencedTheseSigns,
    };
    if (
      !assignedData.injuryFrom ||
      assignedData.headHitLocation.length === 0 ||
      assignedData.immediateSymptomsResulting.length === 0
    ) {
      setErrorMessage(true);
      return;
    }
    setSubmitting(true);
    try {
      const tbiDetailRes = await updateTBIDetail({
        patientId: patient.patientId,
        data: assignedData,
      });
      history.push("/laterSymptoms", {
        date: tbiDetailRes.data.slice(0, tbiDetailRes.data.indexOf("T")),
        fromPatientControl: location.state?.fromPatientControl,
      });
    } catch (err) {
      history.push("/oops");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(40)
      : setOnboardingPercent(30);
    getTBIFormData()
      .then((res) => {
        const { data } = res;
        const processedData = data.map((itemList) => {
          const { category, items } = itemList;
          const translatedItems = items.map(
            (item) => t(`recenttbi2.selector.${category}.${item}`) || item
          );
          return { category, items: translatedItems };
        });
        setReturnedData(processedData);
        initialize(data);
      })
      .catch((err) => {
        console.log(err);
        history.push("/oops");
      })
      .finally(() => setLoading(false));
  }, [t]);

  const getPossibleMessages = () => ({
    mainHeader: {
      tbiPatient: t("recenttbi2.header.patient"),
      caregiver: t("recenttbi2.header.caregiver"),
    },
    sallieText: {
      tbiPatient: t("recenttbi2.sallie.patient"),
      caregiver: t("recenttbi2.sallie.caregiver"),
    },
    tbiFromText: {
      tbiPatient: t("recenttbi2.cause.patient"),
      caregiver: t("recenttbi2.cause.caregiver"),
    },
    headHitLocationText: {
      tbiPatient: t("recenttbi2.location.patient"),
      caregiver: t("recenttbi2.location.caregiver"),
    },
    experiencedTheseSignsText: {
      tbiPatient: t("recenttbi2.signs.patient"),
      caregiver: t("recenttbi2.signs.caregiver"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  const options = {
    tbiFrom: {
      message: "tbiFromText",
      responseNumber: 1,
    },
    headHitLocation: {
      message: "headHitLocationText",
    },
    experiencedTheseSigns: {
      message: "experiencedTheseSignsText",
    },
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <div className="d-block">
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie text={getMessage("sallieText")} />

          {returnedData.map((itemList, i) => {
            const { category } = itemList;
            return (
              <ListSelector
                key={category}
                category={category}
                message={getMessage(options[category].message)}
                list={itemList.items}
                index={i}
                maxSelectAmount={options[category].responseNumber}
                selectedList={selectedData[category]}
                toggleFromSelectedList={toggleFromSelectedList}
                selectOne={switchSingleSelection}
                addOther={addOther}
              />
            );
          })}
          {errorMessage && (
            <Form.Control.Feedback style={{ display: "block" }} type="invalid">
              {t("recenttbi2.choose.error")}
            </Form.Control.Feedback>
          )}
          <SubmitButton disabled={submitting} onClick={onSubmit} type="submit">
            {t("onboarding.saveContinue")} &gt;
          </SubmitButton>
        </div>
      )}
    </>
  );
};

export default RecentTBI2;